export interface TopicAttachment {
  id: string;
  url?: string;
  filename?: string;
}

export interface ContentLink {
  url: string;
  name: string;
  type: string;      // flyer | attachment | reference | metadata
  lang?: string;     // undefined/'' = all
}

export interface TopicDetails {
  title?: Record<string, string>;
  content?: {
    text?: Record<string, string>;
    links?: ContentLink[];
  };
  // Per-language translated structured blobs (stringified JSON), keyed by
  // language code. The canonical/source blob stays in content.text.__structured__.
  structured_i18n?: Record<string, string>;
}

export interface Topic {
  id: string;
  topic_no?: number;
  type_id: number;
  status: 'draft' | 'active' | 'archived';
  details: TopicDetails;
  display_sections?: Array<{ id: number; tag_name: string; sort_index: number }>;
  attachments?: TopicAttachment[];
  edited_at: string; // backend /topic wire field (Prisma editedAt)
  created_at: string;
}

// ---------------------------------------------------------------------------
// Program structured data (kind: 'mortgage'). Redesigned from the 43 real Hot
// Program flyers (see data/flyer/_FIELD_ANALYSIS.md): rate scenarios are
// optional and often multiple → rate_scenarios[]; eligibility is the common
// core → grouped; the long unique tail → highlights/special_features/notes.
// `investor` stays top-level (admin column/filter, iOS filter, backend stats).
// company_info + disclosures are NOT stored per-topic (global company config).
// ---------------------------------------------------------------------------

/** The rate assumptions accompanying a single rate scenario. */
export interface RateAssumptions {
  ltv: number | null;
  loan_amount: number | null;
  credit_score: number | null;
  property_type: string | null;
  transaction_type: string | null;
  occupancy: string | null;
  dti: number | null;
}

/** One advertised rate scenario. A flyer may list several (by LTV/occupancy/doc). */
export interface RateScenario {
  label: string;              // e.g. "Primary 75% LTV", "Interest Only", ""
  loan_type: string;          // "7/6 ARM", "30yr Fixed"
  interest_rate: number | null;
  apr: number | null;
  rate_as_of: string | null;  // ISO YYYY-MM-DD
  points: number | null;
  index: string | null;
  margin: string | null;
  caps: string | null;
  assumptions: RateAssumptions;
}

/** Who/what qualifies — the common program core. One set per program. */
export interface ProgramEligibility {
  loan_purposes: string[];    // purchase / rate-term / cash-out
  occupancy: string[];
  property_types: string[];
  units: string | null;
  borrower_types: string[];
  states_included: string[];
  states_excluded: string[];
  ltv_max: number | null;
  fico_min: number | null;
  dti_max: number | null;
  loan_amount_min: number | null;
  loan_amount_max: number | null;
  documentation: string | null;
  reserves: string | null;
}

export interface StructuredData {
  investor: string | null;       // GMCC categorization (kept top-level for compat)
  program_name: string | null;
  promo_tag: string | null;
  loan_category: string | null;
  rate_scenarios: RateScenario[];
  eligibility: ProgramEligibility;
  highlights: string[];
  special_features: string[];
  notes: string | null;
}

export const EMPTY_ASSUMPTIONS: RateAssumptions = {
  ltv: null,
  loan_amount: null,
  credit_score: null,
  property_type: null,
  transaction_type: null,
  occupancy: null,
  dti: null,
};

export const EMPTY_RATE_SCENARIO: RateScenario = {
  label: '',
  loan_type: '',
  interest_rate: null,
  apr: null,
  rate_as_of: null,
  points: null,
  index: null,
  margin: null,
  caps: null,
  assumptions: { ...EMPTY_ASSUMPTIONS },
};

export const EMPTY_ELIGIBILITY: ProgramEligibility = {
  loan_purposes: [],
  occupancy: [],
  property_types: [],
  units: null,
  borrower_types: [],
  states_included: [],
  states_excluded: [],
  ltv_max: null,
  fico_min: null,
  dti_max: null,
  loan_amount_min: null,
  loan_amount_max: null,
  documentation: null,
  reserves: null,
};

export const EMPTY_STRUCTURED: StructuredData = {
  investor: null,
  program_name: null,
  promo_tag: null,
  loan_category: null,
  rate_scenarios: [],
  eligibility: { ...EMPTY_ELIGIBILITY },
  highlights: [],
  special_features: [],
  notes: null,
};

/** Deep-fill a partial/legacy blob against the empty program shape so callers
 *  always see every field (arrays default to [], eligibility fully populated). */
export function normalizeStructured(raw: unknown): StructuredData {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const elig = (o.eligibility && typeof o.eligibility === 'object' ? o.eligibility : {}) as Record<string, unknown>;
  const scenarios = Array.isArray(o.rate_scenarios) ? o.rate_scenarios : [];
  return {
    ...EMPTY_STRUCTURED,
    ...o,
    eligibility: { ...EMPTY_ELIGIBILITY, ...elig },
    rate_scenarios: scenarios.map((s) => {
      const so = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
      const as = (so.assumptions && typeof so.assumptions === 'object' ? so.assumptions : {}) as Record<string, unknown>;
      return { ...EMPTY_RATE_SCENARIO, ...so, assumptions: { ...EMPTY_ASSUMPTIONS, ...as } };
    }),
    highlights: Array.isArray(o.highlights) ? (o.highlights as string[]) : [],
    special_features: Array.isArray(o.special_features) ? (o.special_features as string[]) : [],
  } as StructuredData;
}

/** Reads the __structured__ JSON blob from details.content.text. */
export function readStructured(d: TopicDetails): StructuredData {
  const raw = d.content?.text?.['__structured__'];
  if (!raw) return normalizeStructured(null);
  try {
    return normalizeStructured(JSON.parse(raw));
  } catch {
    return normalizeStructured(null);
  }
}

/** Writes the __structured__ JSON blob back into details.content.text. */
export function writeStructured(d: TopicDetails, s: StructuredData): TopicDetails {
  const next = JSON.parse(JSON.stringify(d)) as TopicDetails;
  next.content ??= {};
  next.content.text ??= {};
  next.content.text['__structured__'] = JSON.stringify(s);
  return next;
}

// ---------------------------------------------------------------------------
// Per-type structured data. The __structured__ blob is tagged with a `kind`
// discriminator so the editor can render a type-appropriate form and the app
// can tell the shapes apart. Legacy blobs (no kind) are treated as 'mortgage'.
// ---------------------------------------------------------------------------

export type StructuredKind = 'mortgage' | 'schedule' | 'message' | 'none';

export interface ScheduleStructured {
  schedule_datetime: string | null;
  frequency: 'once' | 'weekly' | null;
  weekdays: string[];
  ics_url: string | null;
  session_number: number | null;
  meeting_link: string | null;
  location: string | null;
  presenter: string | null;
  notes: string | null;
}

export const EMPTY_SCHEDULE: ScheduleStructured = {
  schedule_datetime: null,
  frequency: null,
  weekdays: [],
  ics_url: null,
  session_number: null,
  meeting_link: null,
  location: null,
  presenter: null,
  notes: null,
};

export interface MessageStructured {
  author: string | null;
  publish_date: string | null;
  summary: string | null;
}

export const EMPTY_MESSAGE: MessageStructured = {
  author: null,
  publish_date: null,
  summary: null,
};

const STRUCTURED_KINDS: readonly StructuredKind[] = ['mortgage', 'schedule', 'message', 'none'];

/** Reads the raw __structured__ object (any kind) from details, or {} when absent. */
export function readStructuredRaw(d: TopicDetails): Record<string, unknown> {
  const raw = d.content?.text?.['__structured__'];
  if (!raw) return {};
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Writes an arbitrary __structured__ object back into details.content.text. */
export function writeStructuredRaw(d: TopicDetails, obj: Record<string, unknown>): TopicDetails {
  const next = JSON.parse(JSON.stringify(d)) as TopicDetails;
  next.content ??= {};
  next.content.text ??= {};
  next.content.text['__structured__'] = JSON.stringify(obj);
  return next;
}

// ---------------------------------------------------------------------------
// Per-language structured (like title/content, the structured blob is edited +
// translated per language). The SOURCE language lives at the canonical location
// (content.text.__structured__); other languages live at structured_i18n[lang]
// as stringified JSON. Reads fall back to the canonical blob so the editor is
// never empty (numbers come from source).
// ---------------------------------------------------------------------------

export function readStructuredRawForLang(d: TopicDetails, lang: string, sourceLang = 'en'): Record<string, unknown> {
  if (lang === sourceLang) return readStructuredRaw(d);
  const raw = d.structured_i18n?.[lang];
  if (raw) {
    try {
      const o = JSON.parse(raw);
      if (o && typeof o === 'object') return o as Record<string, unknown>;
    } catch { /* fall through to canonical */ }
  }
  return readStructuredRaw(d); // fallback: show the source structure to edit
}

export function writeStructuredRawForLang(
  d: TopicDetails, lang: string, obj: Record<string, unknown>, sourceLang = 'en',
): TopicDetails {
  if (lang === sourceLang) return writeStructuredRaw(d, obj);
  const next = JSON.parse(JSON.stringify(d)) as TopicDetails;
  next.structured_i18n = { ...(next.structured_i18n ?? {}), [lang]: JSON.stringify(obj) };
  return next;
}

/** Deep emptiness: null/''/[]/{} and objects/arrays whose entries are all empty. */
function isEmptyValue(v: unknown): boolean {
  if (v == null || v === '') return true;
  if (Array.isArray(v)) return v.every(isEmptyValue);
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).every(isEmptyValue);
  return false;
}

/** The structured kind stored in the blob; legacy blobs (data, no kind) → 'mortgage'. */
export function readBlobKind(d: TopicDetails): StructuredKind {
  const raw = readStructuredRaw(d);
  if (typeof raw.kind === 'string' && STRUCTURED_KINDS.includes(raw.kind as StructuredKind)) {
    return raw.kind as StructuredKind;
  }
  const hasData = Object.entries(raw).some(([k, v]) => k !== 'kind' && !isEmptyValue(v));
  return hasData ? 'mortgage' : 'none';
}

/**
 * True when a language has any translatable text — a non-empty title or body.
 * Drives the language-switcher completeness dots and the "Only missing"
 * translate shortcut. Ignores the __structured__ blob (not per-language text).
 */
export function langHasContent(d: TopicDetails, code: string): boolean {
  const title = d.title?.[code]?.trim();
  const body = d.content?.text?.[code]?.trim();
  return Boolean(title) || Boolean(body);
}

/** Links of a single type (e.g. 'flyer'), in order. */
export function readLinksOfType(d: TopicDetails, type: string): ContentLink[] {
  return (d.content?.links ?? []).filter((l) => l.type === type);
}

/** Replace all links of `type` with `rows`, preserving links of every other type. */
export function writeLinksOfType(d: TopicDetails, type: string, rows: ContentLink[]): TopicDetails {
  const next = JSON.parse(JSON.stringify(d)) as TopicDetails;
  next.content ??= {};
  const others = (next.content.links ?? []).filter((l) => l.type !== type);
  next.content.links = [...others, ...rows.map((r) => ({ ...r, type }))];
  return next;
}
